import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody } from '@/design-system/components/typography';
import { SxProp, View } from 'dripsy';
import _ from 'lodash';
import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInput } from '@/design-system/components/inputs';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { IssueCardStackParamList, IssueCardStackRoutes } from './type';
import { Dropdown } from '@/design-system/components/dropdown';
import { CardIssueTypeFilters } from '../../options';

export interface IssueCardProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  onData: (data: any) => void;
  account: string;
  tab?: number;
  record?: any;
  func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
}

export const IssueCardWebZeroScreen: React.FC<IssueCardProps> = (props) => {
  const { onClose, onLayout, onData, account, tab, record, func, onReturnData, refresh } = props;
  const { t } = useTranslation('account-management');
  const layout = useContext(LayoutContext);
  const { body, textMb, btnView, btn } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<IssueCardStackParamList>>();

  const [viewShow, setViewShow] = useState(false);
  const onChangeView = () => {
    setViewShow(!viewShow);
  };

  const [issue, setIssue] = useState<any>({
    type: 'physical',
    productId: undefined,
    embossedName: undefined,
    cardSuffix: undefined,
    cardAccountId: account
  });
  const [errorMsg, setErrorMsg] = useState<any>({ type: undefined, productId: undefined, embossedName: undefined, cardSuffix: undefined });

  const changeIssueCard = () => {
    let validate = true;
    if (!issue.productId) {
      setErrorMsg({ ...errorMsg, productId: t('card-account-management::issue-card.instruction-form.text--this-field-is-required') });
      validate = false;
    } else if (!issue.embossedName) {
      setErrorMsg({
        ...errorMsg,
        productId: undefined,
        embossedName: t('card-account-management::issue-card.instruction-form.text--this-field-is-required')
      });
      validate = false;
    }

    if (validate) {
      onData({ data: issue });
      navigation.navigate(IssueCardStackRoutes.Confirm, {
        onLayout,
        data: issue,
        account,
        tab,
        record,
        func,
        onReturnData,
        refresh,
        onChangeView
      });
    }
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader title={t('card-account-management::issue-card.instruction-form.title--issue-card')} closeModal={onClose} />
      <View sx={body}>
        <TextBody sx={textMb} semiBold>
          {t('card-account-management::issue-card.instruction-form.text--instruction-form-issue-card')}
        </TextBody>
        <View sx={{ gap: '$5' }}>
          <View sx={{ zIndex: 99 }}>
            <Dropdown
              value={issue.type}
              label={t('card-account-management::issue-card.instruction-form.text--card-type')}
              options={CardIssueTypeFilters(t)}
              sx={{ width: '100%' }}
              onValueChange={(value) => {
                setIssue({
                  ...issue,
                  type: value
                });
              }}
            />
          </View>
          <UniInput
            value={issue.productId}
            labelText={t('card-account-management::issue-card.instruction-form.text--card-product')}
            errorText={errorMsg.productId}
            onValueChange={(value) => {
              setIssue({
                ...issue,
                productId: value
              });
            }}
          />
          <UniInput
            value={issue.cardSuffix}
            labelText={t('card-account-management::issue-card.instruction-form.text--card-number')}
            errorText={errorMsg.cardSuffix}
            onValueChange={(value) => {
              let cardSuffix = value?.replace(/[^\d]/g, '');
              setIssue({
                ...issue,
                cardSuffix
              });
            }}
          />
          <UniInput
            value={issue.embossedName}
            labelText={t('card-account-management::issue-card.instruction-form.text--embossed-name')}
            errorText={errorMsg.embossedName}
            onValueChange={(value) => {
              setIssue({
                ...issue,
                embossedName: value?.toUpperCase()
              });
            }}
          />
        </View>
        {viewShow && <View sx={{ height: '$1', width: '100%' }} />}
        <View sx={btnView}>
          <UniButton
            sx={btn}
            variant={ButtonVariant.outline1}
            label={t('card-account-management::general.button--cancel')}
            onPress={onClose}
          />
          <UniButton
            sx={btn}
            labelSx={{ color: '$balck' }}
            label={t('card-account-management::issue-card.instruction-form.title--issue-card')}
            onPress={changeIssueCard}
          />
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 40
  },
  btn: {
    width: 216,
    height: 44
  }
};
